import { useState } from "react";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";

type StructureActionsPanelProps = {
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
};

type InsertMode = "above" | "below" | "end";
type CreateType = "section" | "subsection";

export default function StructureActionsPanel({
  onSave,
  onCancel,
  isSaving,
}: StructureActionsPanelProps) {
  const [insertMode, setInsertMode] = useState<InsertMode>("end");
  const [createType, setCreateType] = useState<CreateType | null>(null);
  const [titleInput, setTitleInput] = useState("");
  const [titleError, setTitleError] = useState<string | null>(null);

  const {
    presentManual,
    selectedSectionId,
    selectedSubSectionId,
    createSection,
    createSubSection,
    insertSectionAt,
    insertSubSectionAt,
    moveSectionUp,
    moveSectionDown,
    deleteSection,
    moveSubSectionUp,
    moveSubSectionDown,
    deleteSubSection,
  } = useManualAuthoringStore();

  const isSectionSelected = !!selectedSectionId && !selectedSubSectionId;
  const isSubSelected = !!selectedSectionId && !!selectedSubSectionId;

  /* =========================
     INSERT HELPERS
     ========================= */

  const normalizeTitle = (value: string) =>
    value.replace(/[^a-zA-Z0-9 ]+/g, "").toUpperCase();

  const openCreateModal = (type: CreateType) => {
    setCreateType(type);
    setTitleInput("");
    setTitleError(null);
  };

  const closeCreateModal = () => {
    setCreateType(null);
    setTitleInput("");
    setTitleError(null);
  };

  const handleAddSectionWithTitle = (title: string) => {
    if (!selectedSectionId || insertMode === "end") {
      createSection(title);
      return;
    }

    const index = presentManual.sections.findIndex(
      (s) => s.id === selectedSectionId
    );

    if (index === -1) return;

    const targetIndex = insertMode === "above" ? index : index + 1;
    insertSectionAt(title, targetIndex);
  };

  const handleAddSubSectionWithTitle = (title: string) => {
    if (!selectedSectionId) return;

    const section = presentManual.sections.find(
      (s) => s.id === selectedSectionId
    );

    if (!section) return;

    if (!selectedSubSectionId || insertMode === "end") {
      createSubSection(selectedSectionId, title);
      return;
    }

    const subs = section.subSections ?? [];
    const index = subs.findIndex((s) => s.id === selectedSubSectionId);

    if (index === -1) return;

    const targetIndex = insertMode === "above" ? index : index + 1;
    insertSubSectionAt(selectedSectionId, title, targetIndex);
  };

  const handleCreateConfirm = () => {
    const trimmed = titleInput.trim();
    if (!trimmed) {
      setTitleError("Name is required.");
      return;
    }
    if (createType === "section") {
      handleAddSectionWithTitle(trimmed);
    } else if (createType === "subsection" && selectedSectionId) {
      handleAddSubSectionWithTitle(trimmed);
    }
    closeCreateModal();
  };

  return (
    <div className="nq-panel">

      <div className="nq-panel-title">Structure Actions</div>

      {/* ================= INSERT MODE TOGGLE ================= */}
      <div className="insert-toggle">
        <button
          className={`toggle-btn ${insertMode === "above" ? "active" : ""}`}
          onClick={() => setInsertMode("above")}
        >
          <i className="bi bi-arrow-up" /> Above
        </button>

        <button
          className={`toggle-btn ${insertMode === "below" ? "active" : ""}`}
          onClick={() => setInsertMode("below")}
        >
          <i className="bi bi-arrow-down" /> Below
        </button>

        <button
          className={`toggle-btn ${insertMode === "end" ? "active" : ""}`}
          onClick={() => setInsertMode("end")}
        >
          <i className="bi bi-box-arrow-down" /> End
        </button>
      </div>

      {/* ================= ADD ================= */}
      <div className="nq-panel-section">
        <div
          className="nq-action-item"
          onClick={() => openCreateModal("section")}
        >
          <i className="bi bi-folder-plus nq-action-icon" />
          Add Section
        </div>

        <div
          className="nq-action-item mt-2"
          onClick={() => {
            if (!selectedSectionId) return;
            openCreateModal("subsection");
          }}
        >
          <i className="bi bi-file-earmark-plus nq-action-icon" />
          Add Subsection
        </div>
      </div>

      {/* ================= SECTION SELECTED ================= */}
      {isSectionSelected && selectedSectionId && (
        <div className="nq-panel-section">
          <div className="nq-panel-title">Selected: Section</div>

          <div
            className="nq-action-item"
            onClick={() => moveSectionUp(selectedSectionId)}
          >
            <i className="bi bi-arrow-up nq-action-icon" />
            Move Up
          </div>

          <div
            className="nq-action-item mt-2"
            onClick={() => moveSectionDown(selectedSectionId)}
          >
            <i className="bi bi-arrow-down nq-action-icon" />
            Move Down
          </div>

          <div
            className="nq-action-item danger mt-2"
            onClick={() => deleteSection(selectedSectionId)}
          >
            <i className="bi bi-trash nq-action-icon" />
            Delete Section
          </div>
        </div>
      )}

      {/* ================= SUBSECTION SELECTED ================= */}
      {isSubSelected && selectedSectionId && selectedSubSectionId && (
        <div className="nq-panel-section">
          <div className="nq-panel-title">Selected: Subsection</div>

          <div
            className="nq-action-item"
            onClick={() =>
              moveSubSectionUp(selectedSectionId, selectedSubSectionId)
            }
          >
            <i className="bi bi-arrow-up nq-action-icon" />
            Move Up
          </div>

          <div
            className="nq-action-item mt-2"
            onClick={() =>
              moveSubSectionDown(selectedSectionId, selectedSubSectionId)
            }
          >
            <i className="bi bi-arrow-down nq-action-icon" />
            Move Down
          </div>

          <div
            className="nq-action-item danger mt-2"
            onClick={() =>
              deleteSubSection(selectedSectionId, selectedSubSectionId)
            }
          >
            <i className="bi bi-trash nq-action-icon" />
            Delete Subsection
          </div>
        </div>
      )}

      {/* ================= SAVE / CANCEL ================= */}
      <div className="nq-panel-section">
        <div
          className={`nq-action-item primary ${isSaving ? "is-disabled" : ""}`}
          onClick={() => {
            if (isSaving) return;
            void onSave();
          }}
        >
          <i className="bi bi-check-circle nq-action-icon" />
          {isSaving ? "Saving..." : "Save Hierarchy"}
        </div>

        <div
          className="nq-action-item mt-2"
          onClick={onCancel}
        >
          <i className="bi bi-x-circle nq-action-icon" />
          Cancel
        </div>
      </div>

      {createType && (
        <div className="nq-modal-backdrop" role="dialog" aria-modal="true">
          <div className="nq-modal">
            <div className="nq-modal-title">
              {createType === "section" ? "Add Section" : "Add Subsection"}
            </div>
            <label className="nq-modal-label">
              {createType === "section" ? "Section Name" : "Subsection Name"}
            </label>
            <input
              className="nq-modal-input"
              value={titleInput}
              onChange={(e) => {
                setTitleError(null);
                setTitleInput(normalizeTitle(e.target.value));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateConfirm();
                if (e.key === "Escape") closeCreateModal();
              }}
              autoFocus
              spellCheck
              inputMode="text"
            />
            {titleError && <div className="nq-modal-error">{titleError}</div>}
            <div className="nq-modal-actions">
              <button className="btn btn-sm btn-light" onClick={closeCreateModal}>
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleCreateConfirm}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
